import React, { useCallback, useEffect, useState } from "react";
import styles from "./BusinessBackground.module.scss";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import Icon from "../../common/Icon/Icon";
import { useAppSelector } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";
import Dropdown from "../../common/Dropdown/Dropdown";
import Tooltip from "../../common/Tooltip/Tooltip";
import Editor from "../../common/Editor/Editor";
import { useProfileParams } from "../../../utils/common";
import ABOBackgroundLoader from "../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader";
import { isValidResponse } from "../../../utils/validateAPIResponse";
import {
  LeaderBusinessBackgroundPayload,
  SuccessorType,
  useGetLeaderBusinessBackgroundQuery,
  useSaveLeaderBusinessBackgroundMutation,
} from "../../../redux/services/leaderBusinessBackgroundAPI";

interface BusinessBackgroundProps {
  onChangeTab: (tabID: string) => void;
}

const dropdownOptions = [
  { code: "yes", value: "yes" },
  { code: "no", value: "no" },
  { code: "inprogress", value: "inprogress" },
];
const BusinessBackground: React.FC<BusinessBackgroundProps> = ({
  onChangeTab,
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const { affiliateCode, aboNumber } = useProfileParams();
  const [isEditing, setIsEditing] = useState(false);
  const [successor, setIsSuccessor] = useState<DropdownItem>({
    code: "",
    value: "",
  });
  const [details, setDetails] = useState("");
  const [successorDetails, setSuccessorDetails] = useState("");
  const [isAPIError, setIsAPIError] = useState(false);

  const { data, isFetching, isError } = useGetLeaderBusinessBackgroundQuery(
    { affiliateCode, aboNumber },
    {
      skip: Number(affiliateCode) === 0 || aboNumber === 0 || isAPIError,
      refetchOnMountOrArgChange: true,
    }
  );

  const handleResponse = useCallback(() => {
    if (data && isValidResponse(data.statusCode)) {
      const body = data.body;
      const incomingDetails = body?.businessDetail || "";
      const hasSuccessor = body?.successorType;
      const incomingSuccessorDetails = body?.successorDetail || "";

      setDetails(incomingDetails);
      setSuccessorDetails(incomingSuccessorDetails);
      setIsSuccessor(toDropdownItemFromType(hasSuccessor));
    }
  }, [data]);

  const toSuccessorType = (code?: string | number): SuccessorType => {
    return code === "yes" ? 1 : code === "no" ? 2 : 3;
  };

  const [saveLeaderBusinessBackground] = useSaveLeaderBusinessBackgroundMutation();

  useEffect(() => {
    if (isError) {
      setIsAPIError(true);
      return;
    }

    handleResponse();
  }, [isError, isFetching, handleResponse]);

  if (isFetching) {
    return <ABOBackgroundLoader num={3} heading={t("business_background")} />;
  }

  const handleSave = async () => {
    try {
      const successorType = toSuccessorType(successor?.code) // 1=no,2=yes,3=in progress
      const payload: LeaderBusinessBackgroundPayload = {
        businessDetail: details || "",
        successorType,
        successorDetail: successorType === 1 ? (successorDetails || "") : "",
      };

      const response: any = await saveLeaderBusinessBackground({
        affiliateCode,
        aboNumber,
        payload,
      });

      if (isValidResponse(response?.data?.statusCode)) {
        const body = response.data?.body || {};
        const incomingDetails = body?.businessDetail || "";
        const hasSuccessor = body?.successorType;
        const incomingSuccessorDetails = body?.successorDetail || "";

        setDetails(incomingDetails);
        setSuccessorDetails(incomingSuccessorDetails);
        setIsSuccessor(toDropdownItemFromType(hasSuccessor));
        setIsEditing(false);
      } else {
        setIsAPIError(true);
      }
    } catch (e) {
      setIsAPIError(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSelectDropdownOption = (item: DropdownItem) => {
    setIsSuccessor(item);
  };

  const handleChange = (value: string) => {
    setDetails(value);
  };
  const handleSuccessorChange = (value: string) => {
    setSuccessorDetails(value);
  };

  const handleNavigate = () => {
    onChangeTab("notesdocument");
  };

  const toDropdownItemFromType = (type?: SuccessorType): DropdownItem => {
    if (type === 1) return { code: "yes", value: "yes" };
    if (type === 2) return { code: "no", value: "no" };
    if (type === 3) return { code: "inprogress", value: "inprogress" };
    return { code: "", value: "" };
  };

  return (
    <div className={styles.business_outer_wrapper}>
      <div className={styles.title_wrapper}>
        <div className={styles.title_inner}>
          <div className={styles.progress_bar}>
            <CircularProgressbarWithChildren
              value={data?.body?.completionRate || 0}
              strokeWidth={20}
              className={styles["circular_progress_gradient"]}
              styles={buildStyles({
                trailColor: currentTheme === "light" ? "#D9D9D9" : "#282828",
              })}
            />
          </div>
          <div className={styles.general_info_title}>
            {t("business_background")}
          </div>
        </div>
        <div className={styles.edit_icon_wrapper}>
          {isEditing ? (
            <>
              <span className={styles.cancel_text} onClick={handleCancel}>
                {t("btnCancel")}
              </span>
              <span className={styles.save_text} onClick={handleSave}>
                {t("btnSave")}
              </span>
            </>
          ) : (
            <div
              className={styles.edit_wrapper}
              onClick={() => setIsEditing(true)}
            >
              <Icon
                name="edit_goal_icon"
                size={14}
                color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
                className={styles.search_icon}
              />
              <span className={styles.edit_text}>{t("edit")}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.business_inner_wrapper}>
        <div className={styles.left_wrapper}>
          <span className={styles.background_history}>
            {t("business_background_history")}:
          </span>
          <Editor
            id="addBusinessBackground"
            testId="addBusinessBackgrounddisc"
            isEditing={isEditing}
            value={details}
            onChange={handleChange}
            formatterBarPlaceholder={t("business_history_placeholder")}
            section="growthplan"
            normalize={true}
            htmlToPdfContainerClassName={styles.details_text}
          />
        </div>

        <div className={styles.right_wrapper}>
          <div className={styles.successor_wrapper}>
            <span className={styles.question}>
              {t("business_background_question")}
            </span>
            <span className={styles.successor_dropdown}>
              {isEditing ? (
                <Dropdown
                  dropdownName={t("business_dropdown_placeholder")}
                  items={dropdownOptions}
                  selectedItem={{
                    code: successor?.code ?? "",
                    value: !!successor?.value ? t(`${successor?.value}`) : "",
                  }}
                  handleSelectItemCallback={handleSelectDropdownOption}
                  size="md"
                />
              ) : (
                <span className={styles.selected_item}>
                  {successor?.value ? t(`${successor?.value}`) : ""}
                </span>
              )}
            </span>
            {successor?.code === "yes" && (
              <span className={styles.successor_textarea}>
                <Editor
                  id="addSuccessorDetails"
                  testId="addSuccessorDetailsDesc"
                  isEditing={isEditing}
                  value={successorDetails}
                  onChange={handleSuccessorChange}
                  formatterBarPlaceholder={t("business_successor_detail")}
                  section="successor_details"
                  normalize={true}
                  htmlToPdfContainerClassName={styles.successor_text}
                />
              </span>
            )}
          </div>

          {!isEditing && (
            <div className={styles.succession_planning_wrapper}>
              <Icon
                name={"linked_icon"}
                size={15}
                className={styles.linked_icon}
                color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
              />
              <span className={styles.link_text} onClick={handleNavigate}>
                {t("business_notes_document")}
              </span>

              <div className={`leg_tooltip ${styles.leg_tooltip}`}>
                <Tooltip
                  content={
                    <div className={styles.leg_tooltip}>
                      <div className={styles.tooltip_wrapper}>
                        <div className={styles.tooltip}>
                          <div className={styles.tooltip_text}>
                            {t("business_background_tooltip")}
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                  width="240"
                  height="auto"
                  left="0%"
                  right="0%"
                  bottom="33px"
                  tipPos={true}
                >
                  <Icon
                    name={"info_icon"}
                    size={19}
                    className={styles.info_icon}
                    color={currentTheme === "light" ? "#707070" : "#AEAEB2"}
                  />
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessBackground;
